import { Toggle } from 'AWS-UI-Components-React';
import React from 'react';
import { ASIN_METRIC_TYPE, DEFAULT_TREND_WEEKS, HISTOGRAM_TYPE } from '../../../constants/asin_metric_elements';
import { CHANNELS } from '../../../constants/channels';
import { formatTimeFrameToYYYYMM } from '../../../utils/date-utils';
import logger from '../../../utils/logging';
import { ErrorFlash } from '../../content/error_flash';
import { AsinInfoTable } from './asin_info_table';
import { getAsinMetricsClient } from './asin_metrics_client';
import { extractMonth, generateCompositeKey, getComponentMetricMap, mergeKeysOfObjectsWithSameId } from './asin_metrics_utils';
import { ASINTrendChart } from './asin_trend_chart';
import { GlMetricTable } from './gl_metric_table';
import { HistogramBarChart } from './histogram_bar_chart';

/**
 * Renders an Asin Metric report.
 */
export class AsinMetricReport extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      availableMonths: [formatTimeFrameToYYYYMM(this.props.payload.timeFrame).toString()],
      loadingGlMetric: false,
      loadingPerUnitAsinInfo: false,
      loadingSmapeAsinInfo: false,
      loadingTrendData: false,
      loadingAsinMetric: false,
      loadError: false,
      reportRows:[],
      reportSummaryRow: [],
      perUnitAsinRows:[],
      perUnitAsinComponent:'',
      trendDataRows: [],
      asinMetricRows: [],
      trendLines: [],
      displayInMillions: false,
      displayDetails: false,
      latestSnapshot: 0,
      snapshotToComponentMetricMap: null,
    };
    this.payload = {};
  };

  componentDidMount = () => {
    this.initialClientTasks();
  };

  initialClientTasks = () => {
    this.asinMetricsClient = getAsinMetricsClient();
    const payload = this.prepareAsinMetricPayload();
    this.pullGlMetrics(payload);
  };

  pullGlMetrics = (payload) => {
    this.setState({loadingGlMetric: true});
    this.asinMetricsClient.getGlMetrics(this.processGlMetricResults, this.onFailedRequest, payload)
        .then(() => this.setState({loadingGlMetric: false}));
  };

  pullAsinInfo = (type, bucketId, componentName) => {
    this.setState({loadingPerUnitAsinInfo: true, perUnitAsinComponent: componentName});
    //backend accepts ABS_VARIANCE and NET_VARIANCE; will update in next rev
    const metric = type === ASIN_METRIC_TYPE.ABS_VAR_PER_UNIT ? ASIN_METRIC_TYPE.ABS_VARIANCE : ASIN_METRIC_TYPE.NET_VARIANCE;
    //Backend only accepts Retail not RETAIL
    const channel = CHANNELS.RETAIL === this.props.payload.channel ? 'Retail' : this.props.payload.channel;
    let payload = {marketplaceId: parseInt(this.props.payload.marketplace), channel,
      month:formatTimeFrameToYYYYMM(this.props.payload.timeFrame), gls: this.props.payload.gls,
      componentName: componentName, bucketId: bucketId, limit: 10, metric, snapshot: 20210331};
    if(this.props.payload.channelType){
      payload.channelType = parseInt(this.props.payload.channelType);
    }
    this.asinMetricsClient.getHistogramAsin(this.processAsinInfo, this.onFailedRequest, payload, type);
  };

  prepareAsinMetricPayload = () => {
    let channel = this.props.payload.channel;
    if(channel === CHANNELS.RETAIL) channel = 'Retail'; //Backend only accepts Retail not RETAIL
    let asinPayload = Object.assign({}, {channel, month:formatTimeFrameToYYYYMM(this.props.payload.timeFrame),
      gls:this.props.payload.gls, marketplaceId: this.props.payload.marketplace, trendPeriodInWeeks: DEFAULT_TREND_WEEKS,
      metrics: [ASIN_METRIC_TYPE.NET_FA, ASIN_METRIC_TYPE.NET_LA,
                ASIN_METRIC_TYPE.NET_VAR_PER_UNIT,
                ASIN_METRIC_TYPE.ABS_VAR_PER_UNIT,
                ASIN_METRIC_TYPE.WINSORIZED_NET_VAR_PER_UNIT,
                ASIN_METRIC_TYPE.WINSORIZED_ABS_VAR_PER_UNIT]});
    if(this.props.payload.channelType){
      asinPayload.channelType = parseInt(this.props.payload.channelType);
    }
    this.payload = asinPayload;
    return asinPayload;
  };

  processAsinInfo = (response, type) => {
    if(response && response.data && response.data.asinInfoList) {
      const asinInfoList = response.data.asinInfoList;
      const processedAsinInfoList = asinInfoList.map(info => {return {ASIN: info.id, [type]: info.value};});
      this.setState({perUnitAsinRows: processedAsinInfoList, loadingPerUnitAsinInfo: false});
    } else {
      logger.warn('Missing data from /asinInfo api response');
    }
  };

  /* Convert a list of glMetricItems to component rows
  Sample Input:
  [{
    channel: "Retail"
    componentValues: {associateFees: 196.03714162069707, badDebt: 200, closingFees: 200, ...}
    gl: 201
    marketplaceId: 1
    metric: "SMAPE_ACCURACY"
    month: 202009
    snapshotDate: 20210107}, ...]

    Sample Output:
    [{
      component: associateFees,
      NET_FA: 80,
      NET_LA: 90,
      SMAPE_ACCURACY: 196.03714162069707}, ...]
  */
  processGlMetricResults = (response) => {
    if (response.data && response.data.glMetricItems){
      this.processTrendGraphData(response);
      const glMetricItems = response.data.glMetricItems;
      const [snapshotToComponentMetricMap, latestSnapshot] = getComponentMetricMap(glMetricItems);
      const componentToMetricsMap = snapshotToComponentMetricMap.has(latestSnapshot) ?
                                    snapshotToComponentMetricMap.get(latestSnapshot) : new Map();
      const reportRows = [];
      for (const [key, value] of componentToMetricsMap.entries()){
        reportRows.push({component: key, [ASIN_METRIC_TYPE.NET_FA]: value.get(ASIN_METRIC_TYPE.NET_FA),
                          [ASIN_METRIC_TYPE.NET_LA]: value.get(ASIN_METRIC_TYPE.NET_LA),
                          [ASIN_METRIC_TYPE.FA_UNITS]: value.get(ASIN_METRIC_TYPE.FA_UNITS),
                          [ASIN_METRIC_TYPE.LA_UNITS]: value.get(ASIN_METRIC_TYPE.LA_UNITS),
                          [ASIN_METRIC_TYPE.NET_VAR_PER_UNIT]: value.get(ASIN_METRIC_TYPE.NET_VAR_PER_UNIT),
                          [ASIN_METRIC_TYPE.ABS_VAR_PER_UNIT]: value.get(ASIN_METRIC_TYPE.ABS_VAR_PER_UNIT),
                          [ASIN_METRIC_TYPE.WINSORIZED_NET_VAR_PER_UNIT]: value.get(ASIN_METRIC_TYPE.WINSORIZED_NET_VAR_PER_UNIT),
                          [ASIN_METRIC_TYPE.WINSORIZED_ABS_VAR_PER_UNIT]: value.get(ASIN_METRIC_TYPE.WINSORIZED_ABS_VAR_PER_UNIT)
                        });
      }
      const reportSummaryRow = this.getReportSummaryRow(reportRows);
      this.setState({reportRows, reportSummaryRow, latestSnapshot, snapshotToComponentMetricMap});
    }
  };

  processTrendGraphData = (response) => {
    const glMetricItems = response && response.data && response.data.glMetricItems ? response.data.glMetricItems : [];
    let snapshotMap = new Map();
    for(const glMetricItem of glMetricItems){
      const {snapshot, metric, month, componentValues} = glMetricItem;
      const trendData = Object.keys(componentValues).reduce( (acc, comp) => {
        acc[generateCompositeKey(month, metric, comp)] = componentValues[comp];
        return acc;
      }, {});
      if(!snapshotMap.has(snapshot)) snapshotMap.set(snapshot, {snapshot});
      let snapshotDateRow = snapshotMap.get(snapshot);
      Object.assign(snapshotDateRow, trendData);
    }
    // Merge new trend data with existing data (if any) and update available months, trend lines, and trend data rows used for trend graph
    const trendDataRows = [...this.state.trendDataRows];
    const availableMonths = new Set(this.state.availableMonths);
    let uniqueTrendLines = new Set(this.state.trendLines);
    for(const key of snapshotMap.keys()){
      trendDataRows.push(snapshotMap.get(key));
      Object.keys(snapshotMap.get(key)).filter(k => k !== 'snapshot').forEach(lineId => {
        uniqueTrendLines.add(lineId);
        const month = extractMonth(lineId);
        availableMonths.add(month.toString());
      });
    }
    //Merge keys of trendDataRow with the same snapshotDate
    const combinedRows = mergeKeysOfObjectsWithSameId(trendDataRows, 'snapshot');
    this.setState({trendDataRows: combinedRows, availableMonths: [...availableMonths], trendLines: [...uniqueTrendLines]});
  };

  onFailedRequest = () => {
    this.setState({loadError: true});
  };

  //Only display Contribution Profit Component for summary
  getReportSummaryRow = (reportRows) => {
    const summaryRow = reportRows.filter(row => row.component === 'contributionProfit' ||
      row.component === 'contributionProfitWithoutRefunds');
    return summaryRow;
  };

  onToggleMillions = (chosen) => {
    this.setState({ displayInMillions: chosen.detail.checked});
  };

  onToggleDisplayDetails = (chosen) => {
    this.setState({ displayDetails: chosen.detail.checked});
  };

  onCompareMonths = (months) => {
    let pullRawDataCalls = [];
    months.forEach(
      m => {
        let newPayload = Object.assign({}, this.payload);
        newPayload.month = m;
        pullRawDataCalls.push(this.asinMetricsClient.getGlMetrics(this.processTrendGraphData, this.onFailedRequest, newPayload));
      }
    );
    this.setState({loadingTrendData: true});
    Promise.all(pullRawDataCalls).then(() => this.setState({loadingTrendData: false}));
  };

  render() {

    return (
      <div>
        <div className="awsui-util-action-stripe">
          <div className="awsui-util-action-stripe-title" />
          <div className="awsui-util-action-stripe-group">
            <div className="awsui-util-p-s">
              <Toggle
                label="Display All Components"
                checked={this.state.displayDetails}
                disabled={this.state.loadingGlMetric}
                onChange={this.onToggleDisplayDetails}
              />
            </div>
            <div className="awsui-util-p-s">
              <Toggle
                label="Display in Millions"
                checked={this.state.displayInMillions}
                disabled={this.state.loadingGlMetric}
                onChange={this.onToggleMillions}
              />
            </div>
          </div>
        </div>
        {this.state.loadError && (<ErrorFlash type="ASIN Metrics" />)}
        <GlMetricTable tableName="Contribution Profit Summary"
                       items={this.state.reportSummaryRow}
                       loading={this.state.loadingGlMetric}
                       displayInMillions={this.state.displayInMillions}
                       payload={this.props.payload}
                       downloadEnabled={false}
        />
        { this.state.displayDetails && (
          <GlMetricTable tableName="All Components"
                         items={ this.state.reportRows }
                         loading={ this.state.loadingGlMetric }
                         displayInMillions={ this.state.displayInMillions }
                         payload={this.props.payload}
                         downloadEnabled={true}
                         latestSnapshot={this.state.latestSnapshot}
          />
        )}
        <ASINTrendChart data={this.state.trendDataRows} metricName="GL Metric"
                        snapshotToComponentMetricMap={this.state.snapshotToComponentMetricMap}
                        loading={this.state.loadingGlMetric || this.state.loadingTrendData}
                        lines={this.state.trendLines} availableMonths={this.state.availableMonths}
                        displayInMillions={this.state.displayInMillions}
                        payload={this.props.payload}
                        onCompareMonths={this.onCompareMonths} />
        <HistogramBarChart histogramName="Per Unit Variance ASIN Histogram"
                           histogramType={HISTOGRAM_TYPE.PERUNIT}
                           payload={this.props.payload}
                           pullAsinInfo={this.pullAsinInfo} />
        <AsinInfoTable tableName={"Sample ASIN Info"} items={this.state.perUnitAsinRows}
                       loading={this.state.loadingPerUnitAsinInfo}
                       displayInMillions={this.state.displayInMillions}
                       payload={this.props.payload}
                       component={this.state.perUnitAsinComponent} />
      </div>
    );
  };
}



// WEBPACK FOOTER //
// ./src/components/reports/asin_metrics/asin_metric_report.js